import { Component, HostListener, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '../../@services/notification.service';
import { LocalstorageService } from '../../@services/localstorage.service';
import { StaffMeal, StaffMealControllerService, StaffMealSearchResult } from '../../@services/gen';
import { finalize, timeout, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

export interface StaffMealDialogData {
  totalAmount: number;
}

export interface StaffMealDialogResult {
  staffMeal: StaffMeal;
  staffMealAmount: number;
  remainingAmount: number;
}

@Component({
  selector: 'app-staff-meal-dialog',
  templateUrl: './staff-meal-dialog.component.html',
  styleUrls: ['./staff-meal-dialog.component.scss'],
})
export class StaffMealDialogComponent {
  title = 'Staff Meal';
  isMobile: boolean = false;

  // Search state management
  searchTerm: string = '';
  searchResults: StaffMealSearchResult[] = [];
  showSearchResults: boolean = false;
  selectedStaff: StaffMealSearchResult | null = null;
  isSearching: boolean = false;

  // Current staff meal (after selection)
  staffMeal: any = null;
  merchantName: string = '';
  merchantDisplayName: string = '';
  isFromBranch: boolean = false;

  // Calculated amounts
  staffMealAmount: number = 0;
  remainingAmount: number = 0;

  constructor(
    private dialogRef: MatDialogRef<StaffMealDialogComponent>,
    private ns: NotificationService,
    private ls: LocalstorageService,
    private staffMealService: StaffMealControllerService,
    @Inject(MAT_DIALOG_DATA) public data: StaffMealDialogData
  ) {
    this.checkIsMobile();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.checkIsMobile();
  }

  private checkIsMobile() {
    this.isMobile = window.innerWidth < 768;
  }

  search(event?) {
    if (event) {
      event.preventDefault();
    }

    if (!this.searchTerm || this.searchTerm.trim().length === 0) {
      this.ns.showNotification({ message: 'Please enter staff number, username, or name' });
      return;
    }

    this.isSearching = true;
    this.showSearchResults = false;
    this.searchResults = [];
    this.selectedStaff = null;
    this.staffMeal = null;
    this.staffMealAmount = 0;
    this.remainingAmount = 0;

    const searchParams = {
      searchTerm: this.searchTerm.trim(),
      merchantId: this.ls.getMerchant().id,
    };

    this.staffMealService
      .searchStaffMealsPost(searchParams)
      .pipe(
        timeout(10000),
        catchError(error => {
          console.error('Staff meal search error:', error);
          if (error.name === 'TimeoutError') {
            this.ns.showNotification({ message: 'Search timed out. Please try again.' });
          } else {
            this.ns.showNotification({ message: 'Error searching for staff: ' + (error.message || 'Unknown error') });
          }
          return of([]);
        }),
        finalize(() => {
          this.isSearching = false;
        })
      )
      .subscribe((results: StaffMealSearchResult[]) => {
        if (!results || results.length === 0) {
          this.ns.showNotification({ message: 'No staff found matching "' + this.searchTerm + '"' });
          this.showSearchResults = false;
          return;
        }

        if (results.length === 1) {
          // Only one result, auto-select it
          this.selectStaff(results[0]);
        } else {
          // Multiple results, show selection list
          this.searchResults = results;
          this.showSearchResults = true;
          this.ns.showNotification({ message: results.length + ' staff members found. Please select one.' });
        }
      });
  }

  selectStaff(result: StaffMealSearchResult) {
    this.selectedStaff = result;
    this.showSearchResults = false;

    // Map search result to staffMeal format (for compatibility with existing code)
    this.staffMeal = {
      id: result.staffMealId,
      balance: result.balance,
      totalAmount: result.totalAmount,
      merchant: {
        id: result.merchantId,
        name: result.merchantName
      },
      hq: {
        id: result.hqId
      },
      staffMealConfig: {
        isAllowStaffMeal: result.allowed
      }
    };

    // Set merchant display information
    this.merchantName = result.merchantName || '';
    this.merchantDisplayName = result.merchantDisplayName || '';
    this.isFromBranch = result.fromBranch || false;

    // Calculate and display usage
    this.calculateAmounts();
    this.ns.showNotification({ message: 'Staff selected: ' + result.staffNo + ' - ' + result.staffName });
  }

  private calculateAmounts() {
    if (!this.staffMeal) {
      this.staffMealAmount = 0;
      this.remainingAmount = this.data.totalAmount;
      return;
    }

    // Staff meal deduction is the minimum of available balance and total order amount
    const availableBalance = this.staffMeal.balance || 0;
    this.staffMealAmount = Math.min(availableBalance, this.data.totalAmount);
    // Remaining amount to be paid with other methods
    this.remainingAmount = this.data.totalAmount - this.staffMealAmount;
  }

  clearSearch() {
    this.searchTerm = '';
    this.searchResults = [];
    this.showSearchResults = false;
    this.selectedStaff = null;
    this.staffMeal = null;
    this.merchantName = '';
    this.merchantDisplayName = '';
    this.staffMealAmount = 0;
    this.remainingAmount = this.data.totalAmount;
  }

  submit() {
    if (!this.staffMeal || !this.selectedStaff) {
      this.ns.showNotification({ message: 'Please select a staff member first' });
      return;
    }

    if (this.staffMealAmount <= 0) {
      this.ns.showNotification({ message: 'No staff meal balance available' });
      return;
    }

    const result: StaffMealDialogResult = {
      staffMeal: this.staffMeal,
      staffMealAmount: this.staffMealAmount,
      remainingAmount: this.remainingAmount,
    };

    this.dialogRef.close(result);
  }
}
